import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useInvestigationsStoreApi } from '@/stores/investigations-store-provider'
import type { Investigation } from '@/types/investigation'

export interface UseInvestigationDeleteResult {
  /** The investigation pending confirmation, or null when the dialog is closed. */
  deletingInv: Investigation | null
  /** True while the remove mutation is in flight. */
  deleting: boolean
  /** Open the confirmation dialog for the given investigation. */
  requestDelete: (inv: Investigation) => void
  /** Close the confirmation dialog (no-op while a delete is in flight). */
  cancelDelete: () => void
  /** Run the remove mutation for the pending investigation. */
  confirmDelete: () => Promise<void>
}

/**
 * Owns the delete flow for a saved investigation: the pending-target state, the
 * in-flight flag, and the store mutation with success/error toasts. Keeps the
 * page container free of imperative store wiring.
 */
export function useInvestigationDelete(): UseInvestigationDeleteResult {
  const { t } = useTranslation()
  const storeApi = useInvestigationsStoreApi()
  const [deletingInv, setDeletingInv] = useState<Investigation | null>(null)
  const [deleting, setDeleting] = useState(false)

  const requestDelete = useCallback((inv: Investigation) => setDeletingInv(inv), [])

  const cancelDelete = useCallback(() => {
    setDeleting(prev => {
      if (!prev) setDeletingInv(null)
      return prev
    })
  }, [])

  const confirmDelete = useCallback(async () => {
    if (!deletingInv) return
    setDeleting(true)
    try {
      await storeApi.getState().remove(deletingInv.id)
      toast.success(t('investigations.page.delete.success'))
      setDeletingInv(null)
    } catch (err) {
      toast.error(t('investigations.page.delete.failed', { message: (err as Error).message }))
    } finally {
      setDeleting(false)
    }
  }, [deletingInv, storeApi, t])

  return { deletingInv, deleting, requestDelete, cancelDelete, confirmDelete }
}
